import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_TYPE, CostCentre, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { BusinessUnitService, CostCentreService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class CostCentreItemComponentBase extends CrudItemComponent<CostCentre, string> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];

  typeSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected costCentreService: CostCentreService,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(COST_CENTRE_TYPE, fb, elementRef, router, route, userStorage, dialog, costCentreService, injector, snackbar);
  }

  createNewItem() {
    return new CostCentre();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      ccId: [null, []],
      businessUnit: [null, []],
      acronym: [null, []],
      closed: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      lockVersion: [null, []],
      name: [null, [requiredValidator,]],
      type: [null, []],
      updatedBy: [null, []],
      updatedDate: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.ccId?.disable();
    }
  }
  
  ccIdChanging(value: string, unsetDependents: boolean = true) {}
  
  ccIdChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.ccIdChangedByUser(value, unsetDependents);
    }
  }  
  
  ccIdChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  acronymChanging(value: string, unsetDependents: boolean = true) {}
  
  acronymChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.acronymChangedByUser(value, unsetDependents);
    }
  }  
  
  acronymChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  closedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  closedChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.closedChangedByUser(value, unsetDependents);
    }
  }  
  
  closedChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  nameChanging(value: string, unsetDependents: boolean = true) {}
  
  nameChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.nameChangedByUser(value, unsetDependents);
    }
  }  
  
  nameChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  typeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('COST_CENTRE_TYPE').subscribe(
      data => {
        this.typeSelectionList = data;
      }
    );
  }

  typeChanging(value: string, unsetDependents: boolean = true) {}
  
  typeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.typeChangedByUser(value, unsetDependents);
    }
  }  
  
  typeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get ccId() {
    return this.control('ccId');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get acronym() {
    return this.control('acronym');
  }
  
  get closed() {
    return this.control('closed');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get name() {
    return this.control('name');
  }
  
  get type() {
    return this.control('type');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
}





